import { isRejectedWithValue, type Middleware } from '@reduxjs/toolkit';
import { rsosApi } from '@rsos/reduxDataStore/src/features/rsosApi';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import { createFirstAttemptStartTimes } from './firstAttemptStartTimes';

/**
 * Reads the HTTP status from an RTK Query rejection payload.
 *
 * fetchBaseQuery HTTP failures carry a numeric status. fetchBaseQuery's other
 * failure modes ship a string status: 'FETCH_ERROR' (network), 'PARSING_ERROR'
 * (JSON parse), 'TIMEOUT_ERROR', 'CUSTOM_ERROR' (queryFn rejection).
 * Throws inside `transformResponse` / `queryFn` / `prepareHeaders` are wrapped
 * by RTK Query's thunk and surface as the thrown value itself (e.g. a ZodError)
 * with no `status` field; the caller handles that case via the undefined return.
 */
function getStatus(payload: unknown): number | string | undefined {
  if (payload && typeof payload === 'object' && 'status' in payload) {
    const status = (payload as { status: unknown }).status;
    if (typeof status === 'number' || typeof status === 'string') return status;
  }
  return undefined;
}

interface RTKQueryRejectionMeta {
  arg?: {
    endpointName?: string;
    originalArgs?: unknown;
  };
  baseQueryMeta?: {
    request?: { url?: string };
  };
  requestId?: string;
}

// A fetch cancelled by navigation / tab close / reload surfaces from
// fetchBaseQuery as a FETCH_ERROR ("TypeError: Failed to fetch"), indistinguishable
// from a genuine mid-session connectivity drop. Record whether the page was
// unloading when the error fired so the two can be told apart in Sentry.
let isPageUnloading = false;
if (typeof window !== 'undefined') {
  const markUnloading = () => {
    isPageUnloading = true;
  };
  window.addEventListener('pagehide', markUnloading);
  window.addEventListener('beforeunload', markUnloading);
}

const SETTLED_WINDOW_MS = 60_000;

/**
 * When each in-flight request's *first* attempt started, by `meta.requestId`.
 *
 * One `requestId` covers one RTK Query thunk, and rsosApi wraps its base query
 * in `retry({ maxRetries: 2 })` — so the retries and the backoff sleeps between
 * them all happen inside that single thunk, between its `/pending` and its
 * settle action. Subtracting these timestamps therefore yields
 * `elapsedMsSinceFirstAttempt`: the whole 3-attempt cycle, not one fetch.
 */
const firstAttemptStartTimes = createFirstAttemptStartTimes({
  maxEntries: 500,
  // A request still unsettled after this long is assumed lost — its settle
  // action never arrived, e.g. the thunk was torn down.
  ttlMs: 5 * 60_000,
});
/** Every rsosApi request that settled within the trailing `SETTLED_WINDOW_MS`. */
const recentlySettled: { at: number; reachedServer: boolean }[] = [];
let lastServerResponseAt: number | undefined;

/**
 * Clears the module-level request diagnostics. Exported for tests, which would
 * otherwise carry one case's timing/window state into the next.
 */
export function resetRequestDiagnostics(): void {
  firstAttemptStartTimes.clear();
  recentlySettled.length = 0;
  lastServerResponseAt = undefined;
  isPageUnloading = false;
}

/**
 * What the window tracks is *reached the server*, not *succeeded*: an HTTP 403
 * or a PARSING_ERROR both prove the connection worked. Only FETCH_ERROR and
 * TIMEOUT_ERROR mean nothing came back. A fulfilled request has no status, and
 * plainly reached the server.
 */
function didReachServer(status: number | string | undefined): boolean {
  return status !== 'FETCH_ERROR' && status !== 'TIMEOUT_ERROR';
}

function recordSettled(status: number | string | undefined, now: number): void {
  const reachedServer = didReachServer(status);
  if (reachedServer) lastServerResponseAt = now;
  recentlySettled.push({ at: now, reachedServer });
  while (
    recentlySettled.length &&
    now - recentlySettled[0].at > SETTLED_WINDOW_MS
  ) {
    recentlySettled.shift();
  }
}

/**
 * Whether any *other* rsosApi request got a response while this one was
 * running. This is what separates the two causes an opaque `TypeError: Failed
 * to fetch` collapses together: if nothing at all got through for the request's
 * whole lifetime the client lost its connection, whereas a transport failure
 * alongside concurrent responses is selective and points at one endpoint or the
 * edge.
 */
function getWindowVerdict(firstAttemptStartedAt?: number): string {
  if (firstAttemptStartedAt === undefined) return 'unknown';
  return lastServerResponseAt !== undefined &&
    lastServerResponseAt >= firstAttemptStartedAt
    ? 'others-reached-server'
    : 'nothing-reached-server';
}

/**
 * Buckets the full retry cycle.
 *
 * Boundaries track the floor this value cannot go below, rather than round
 * numbers. With `maxRetries: 2` the two backoff sleeps alone total 720-2520ms
 * (`~~((random() + 0.4) * (300 << attempt))`), so three instantly-refused
 * attempts still land near a second and nothing can reach a sub-second bucket.
 *
 * `<3s` therefore reads as "every attempt failed as fast as it could" — refused
 * / no route / DNS, i.e. the link is down. The upper buckets read as "the
 * attempts hung": the connection was accepted and never answered, which points
 * at the edge (idle timeout, GOAWAY) rather than a dropped WAN link.
 */
function bucketElapsedSinceFirstAttempt(
  elapsedMsSinceFirstAttempt?: number,
): string {
  if (elapsedMsSinceFirstAttempt === undefined) return 'unknown';
  if (elapsedMsSinceFirstAttempt < 3_000) return '<3s';
  if (elapsedMsSinceFirstAttempt < 10_000) return '3-10s';
  if (elapsedMsSinceFirstAttempt < 30_000) return '10-30s';
  return '>=30s';
}

function bucketCount(count: number): string {
  if (count <= 1) return '1';
  if (count <= 5) return '2-5';
  if (count <= 20) return '6-20';
  return '>20';
}

/**
 * Browser-side context the opaque `TypeError: Failed to fetch` message hides.
 * Attached to every reported RTK Query error so network failures become
 * diagnosable — was the client offline, was the tab backgrounded, was the
 * request cancelled by page unload? There is no clean success/failure rate for
 * these requests in APM / RUM / access logs, so we characterize them from the
 * client instead.
 */
function getClientContext(): Record<string, unknown> {
  const nav =
    typeof navigator !== 'undefined'
      ? (navigator as Navigator & { connection?: { effectiveType?: string } })
      : undefined;
  return {
    onLine: nav?.onLine,
    effectiveType: nav?.connection?.effectiveType,
    visibilityState:
      typeof document !== 'undefined' ? document.visibilityState : undefined,
    isPageUnloading,
  };
}

/**
 * Sentry indexes tags but not `extra`, so everything needed to break an issue
 * down across thousands of events — rather than read one sampled event at a
 * time — is duplicated here. Values are bucketed and enumerable on purpose:
 * unbounded tag values blow up Sentry's tag cardinality and stop being useful
 * as a breakdown.
 */
function getTags(
  endpointName: string,
  status: number | string | undefined,
  elapsedMsSinceFirstAttempt: number | undefined,
  windowVerdict: string,
): Record<string, string> {
  const client = getClientContext();
  const transportFailures = recentlySettled.filter(
    s => !s.reachedServer,
  ).length;
  return {
    'rtk.endpoint': endpointName,
    'rtk.status': String(status ?? 'error'),
    'rtk.elapsed_since_first_attempt': bucketElapsedSinceFirstAttempt(
      elapsedMsSinceFirstAttempt,
    ),
    'net.online': String(client.onLine ?? 'unknown'),
    'net.effective_type': String(client.effectiveType ?? 'unknown'),
    'net.window': windowVerdict,
    'net.transport_failures': bucketCount(transportFailures),
    'page.visibility': String(client.visibilityState ?? 'unknown'),
    'page.unloading': String(isPageUnloading),
  };
}

/**
 * Catches every RTK Query mutation/query rejection from rsosApi (and its
 * injectEndpoints extensions) and reports the failure to Sentry as a captured
 * exception with the request context attached as `extra`. Reporting only — no
 * operator-facing toast.
 *
 * Scoped to rsosApi only via an action-type prefix check; standalone
 * createApi instances (transcriptionApi, displaySchemaApi, radiosApi,
 * getSuggestedAddressesApi, transcriptFeedbackApi) are unaffected by this PR.
 *
 * ## Opting out specific status codes
 *
 * If the backend legitimately returns an error code as a normal state for a
 * particular endpoint (e.g. a 404 that means "no record exists yet"), tag the
 * rejected payload with `isExpectedError: true` from that endpoint's
 * `transformErrorResponse`:
 *
 *     transformErrorResponse: error =>
 *       typeof error.status === 'number' && error.status === 404
 *         ? { ...error, isExpectedError: true }
 *         : error,
 *
 * The consumer still receives the rejection (so the hook's `error` field
 * works normally), but this middleware passes it through without logging it
 * to Sentry.
 *
 * Note: `transformErrorResponse` only runs on errors that flow out of the
 * baseQuery (HTTP failures, fetch/timeout errors). Throws inside
 * `transformResponse` (e.g. zod schema parse failures on a 200) take a
 * different path and still log — which is what we want, since a schema
 * mismatch is a real bug.
 */
export const rtkQueryErrorMiddleware: Middleware = () => next => action => {
  const type = String((action as { type?: unknown })?.type ?? '');
  if (!type.startsWith(`${rsosApi.reducerPath}/`)) {
    return next(action);
  }

  const meta = ((action as { meta?: unknown }).meta ??
    {}) as RTKQueryRejectionMeta;
  const now = Date.now();

  if (type.endsWith('/pending')) {
    if (meta.requestId) firstAttemptStartTimes.set(meta.requestId, now);
    return next(action);
  }

  if (type.endsWith('/fulfilled')) {
    // Discard the start time: only failures are reported, so nothing needs it.
    if (meta.requestId) firstAttemptStartTimes.take(meta.requestId);
    recordSettled(undefined, now);
    return next(action);
  }

  if (!isRejectedWithValue(action)) {
    // Rejections without a value are RTK Query bookkeeping (a `condition`
    // short-circuit, an aborted thunk) rather than a request that ran and
    // failed, so they neither report nor count toward the failure window.
    return next(action);
  }

  const firstAttemptStartedAt = meta.requestId
    ? firstAttemptStartTimes.take(meta.requestId)
    : undefined;
  const status = getStatus(action.payload);
  // Read the verdict before this request joins the window, so it describes the
  // other requests that overlapped it rather than itself.
  const windowVerdict = getWindowVerdict(firstAttemptStartedAt);
  // Recorded before the opt-out checks below: an endpoint that opts out of
  // reporting still tells us whether the client could reach the server.
  recordSettled(status, now);

  if (
    action.payload &&
    typeof action.payload === 'object' &&
    'isExpectedError' in action.payload &&
    (action.payload as { isExpectedError?: unknown }).isExpectedError === true
  ) {
    return next(action);
  }

  const endpointName = meta.arg?.endpointName ?? 'unknown';
  const url = meta.baseQueryMeta?.request?.url ?? '';

  // 401s are owned by baseQueryWithReauth in rsosApi: it refreshes the
  // access/product token and, if that fails, clears auth storage and logs
  // the user out. Reporting them here too is noise for what is a normal
  // session-expiry path.
  if (status === 401) {
    return next(action);
  }

  const elapsedMsSinceFirstAttempt =
    firstAttemptStartedAt === undefined
      ? undefined
      : now - firstAttemptStartedAt;

  // Capture as a real exception (not console.error): the message becomes the
  // Sentry issue title verbatim, instead of captureConsoleIntegration
  // appending a "[object Object]" stringification of the context object. The
  // fingerprint keeps each endpoint/status in its own issue, since synthetic
  // exceptions captured from this single line would otherwise all group by
  // stack trace.
  captureExceptionWithScope(
    new Error(
      `[unhandled RTK Query error] ${endpointName} ${status ?? 'error'}`,
    ),
    {
      endpointName,
      status,
      url,
      payload: action.payload,
      args: meta.arg?.originalArgs,
      client: getClientContext(),
      // Whole thunk lifetime: three attempts plus the backoff sleeps between
      // them, and on a 401 the token-refresh detour as well.
      elapsedMsSinceFirstAttempt,
      settledWindow: {
        windowMs: SETTLED_WINDOW_MS,
        serverResponses: recentlySettled.filter(s => s.reachedServer).length,
        transportFailures: recentlySettled.filter(s => !s.reachedServer).length,
        msSinceLastServerResponse:
          lastServerResponseAt === undefined
            ? undefined
            : now - lastServerResponseAt,
      },
    },
    ['rtk-query-error', endpointName, String(status ?? 'error')],
    getTags(endpointName, status, elapsedMsSinceFirstAttempt, windowVerdict),
  );

  return next(action);
};
